import moment from "moment";
import { WeekData } from "../types/rentalCarDatesRangeInput.types";

/**
 * Calculate the number of days for a week interval based on time comparison.
 * If dropoff time > pickup time: 6 days (e.g., pickup 9am, dropoff 5pm = same week, 6 days)
 * If dropoff time <= pickup time: 7 days (e.g., pickup 9pm, dropoff 12am = next day starts new week, 7 days)
 */
export const getWeekIntervalDays = (startTime?: string, endTime?: string): number => {
  // Default to 6 days if times are missing or invalid
  if (!startTime || !endTime || !startTime.includes(':') || !endTime.includes(':')) return 6;
  
  const startParts = startTime.split(':').map(Number);
  const endParts = endTime.split(':').map(Number);
  
  const startHour = startParts[0];
  const startMin = startParts[1] || 0;
  const endHour = endParts[0];
  const endMin = endParts[1] || 0;
  
  // Return default if any values are NaN
  if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) return 6;
  
  const startTimeInMinutes = startHour * 60 + startMin;
  const endTimeInMinutes = endHour * 60 + endMin;
  
  // If dropoff time > pickup time: 6 days, else 7 days
  return endTimeInMinutes > startTimeInMinutes ? 6 : 7;
};

export const getWeeksInMonth = (
  date: Date,
  unavailableDates: Date[] = []
): WeekData[] => {
  const startOfMonth = moment(date).startOf("month");
  const endOfMonth = moment(date).endOf("month");
  const today = moment().startOf("day");
  const weeks: WeekData[] = [];

  // Helper function to check if a date is unavailable
  const isDateUnavailable = (date: moment.Moment): boolean => {
    return unavailableDates.some(
      (unavailableDate) =>
        moment(unavailableDate).startOf("day").isSame(date, "day")
    );
  };

  // Check if we're viewing a month after the current month
  const isAfterCurrentMonth = startOfMonth.isAfter(today, "month");
  const isCurrentMonth = startOfMonth.isSame(today, "month");
  
  // Check if today is unavailable to determine the actual week start
  const todayIsUnavailable = isDateUnavailable(today);
  const actualWeekStart = todayIsUnavailable ? today.clone().add(1, "day") : today.clone();
  
  if (isAfterCurrentMonth) {
    // For future months, calculate 7-day intervals from the actual current week start
    const daysSinceActualStart = startOfMonth.diff(actualWeekStart, "days");
    const daysIntoWeek = daysSinceActualStart % 7;
    
    let current: moment.Moment;
    if (daysIntoWeek === 0) {
      current = startOfMonth.clone();
    } else {
      current = startOfMonth.clone().subtract(daysIntoWeek, "days");
    }
    
    let weekNum = 1;
    while (weekNum <= 7) {
      const weekStart = current.clone();
      const weekEnd = weekStart.clone().add(7, "days");
      
      const weekOverlapsMonth = 
        (weekStart.isSameOrAfter(startOfMonth, "day") && weekStart.isSameOrBefore(endOfMonth, "day")) ||
        (weekEnd.isSameOrAfter(startOfMonth, "day") && weekEnd.isSameOrBefore(endOfMonth, "day"));

      if (weekOverlapsMonth) {
        weeks.push({
          weekNum,
          start: weekStart.toDate(),
          end: weekEnd.toDate(),
          isPast: false,
          isCurrentWeek: false,
          effectiveStart: weekStart.toDate(),
          effectiveEnd: weekEnd.toDate(),
        });
      }

      current.add(7, "days");
      weekNum++;

      if (weekNum > 1 && current.isAfter(endOfMonth, "day") && !weekOverlapsMonth) {
        break;
      }
    }
  } else {
    // For current month or past months
    // All weeks are calculated as 7-day intervals from today
    
    // Generate past weeks (counting backwards from current week start for proper overlap)
    const pastWeeks: WeekData[] = [];
    let pastWeekEnd = actualWeekStart.clone();
    let pastWeekNum = 1;
    
    while (pastWeekNum <= 10) { // Generate enough past weeks
      const pastWeekStart = pastWeekEnd.clone().subtract(6, "days");
      
      const weekOverlapsMonth = 
        (pastWeekStart.isSameOrAfter(startOfMonth, "day") && pastWeekStart.isSameOrBefore(endOfMonth, "day")) ||
        (pastWeekEnd.isSameOrAfter(startOfMonth, "day") && pastWeekEnd.isSameOrBefore(endOfMonth, "day"));

      if (weekOverlapsMonth) {
        pastWeeks.unshift({
          weekNum: pastWeekNum,
          start: pastWeekStart.toDate(),
          end: pastWeekEnd.toDate(),
          isPast: true,
          isCurrentWeek: false,
          effectiveStart: pastWeekStart.toDate(),
          effectiveEnd: pastWeekEnd.toDate(),
        });
      }

      pastWeekEnd = pastWeekStart.clone();
      pastWeekNum++;
      
      if (pastWeekEnd.isBefore(startOfMonth, "day")) {
        break;
      }
    }
    
    // Add current week (starts today or next day if today is unavailable)
    const currentWeekEnd = actualWeekStart.clone().add(6, "days");
    
    if (isCurrentMonth) {
      weeks.push({
        weekNum: pastWeeks.length + 1,
        start: actualWeekStart.toDate(),
        end: currentWeekEnd.toDate(),
        isPast: false,
        isCurrentWeek: true,
        effectiveStart: actualWeekStart.toDate(),
        effectiveEnd: currentWeekEnd.toDate(),
      });
    }
    
    let futureWeekStart = currentWeekEnd.clone();
    let futureWeekNum = 1;
    
    while (futureWeekNum <= 10) {
      const futureWeekEnd = futureWeekStart.clone().add(7, "days");
      
      const weekOverlapsMonth = 
        (futureWeekStart.isSameOrAfter(startOfMonth, "day") && futureWeekStart.isSameOrBefore(endOfMonth, "day")) ||
        (futureWeekEnd.isSameOrAfter(startOfMonth, "day") && futureWeekEnd.isSameOrBefore(endOfMonth, "day"));

      if (weekOverlapsMonth) {
        weeks.push({
          weekNum: pastWeeks.length + 1 + futureWeekNum,
          start: futureWeekStart.toDate(),
          end: futureWeekEnd.toDate(),
          isPast: false,
          isCurrentWeek: false,
          effectiveStart: futureWeekStart.toDate(),
          effectiveEnd: futureWeekEnd.toDate(),
        });
      }

      futureWeekStart.add(7, "days");
      futureWeekNum++;
      
      if (futureWeekStart.isAfter(endOfMonth, "day")) {
        break;
      }
    }
    
    // Combine past and current/future weeks
    weeks.unshift(...pastWeeks);
    
    // Renumber weeks sequentially
    weeks.forEach((week, index) => {
      week.weekNum = index + 1;
    });
  }

  return weeks;
};

export function getAllWeeksForSelection(
  selectedWeeks: string[],
  unavailableDates: Date[] = []
): WeekData[] {
  if (selectedWeeks.length === 0) return [];

  const sorted = [...selectedWeeks].sort();
  const minDate = moment(sorted[0]);
  const maxDate = moment(sorted[sorted.length - 1]);
  const allWeeks: WeekData[] = [];
  const seenKeys = new Set<string>();

  let current = minDate.clone().startOf("month");
  const last = maxDate.clone().startOf("month");
  while (current.isSameOrBefore(last, "month")) {
    const monthWeeks = getWeeksInMonth(current.toDate(), unavailableDates);
    // Deduplicate weeks by their key (same week can appear in multiple months)
    for (const week of monthWeeks) {
      const keyDate = week.isCurrentWeek ? week.effectiveStart : week.start;
      const key = moment(keyDate).format("YYYY-MM-DD");
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        allWeeks.push(week);
      }
    }
    current.add(1, "month");
  }
  return allWeeks;
}

export const getSelectedWeeksDateRange = (
  selectedWeeks: string[], // array of "YYYY-MM-DD"
  allWeeks: WeekData[],
  startTime?: string, // "HH:mm" format
  endTime?: string, // "HH:mm" format
): { start: Date; end: Date } | null => {
  if (selectedWeeks.length === 0) return null;

  // Find matching WeekData - check against effectiveStart for current week, start for others
  const selectedWeekData = allWeeks.filter((w) => {
    const keyToCheck = w.isCurrentWeek 
      ? moment(w.effectiveStart).format("YYYY-MM-DD")
      : moment(w.start).format("YYYY-MM-DD");
    return selectedWeeks.includes(keyToCheck);
  });
  
  // If no matching weeks found in allWeeks, treat the selectedWeeks keys as direct dates
  // This handles cases where keys are interval-based and don't match calendar weeks
  if (selectedWeekData.length === 0) {
    const sortedKeys = [...selectedWeeks].sort();
    const firstKey = sortedKeys[0];
    const numWeeks = sortedKeys.length;
    
    // Parse start time
    let startHour = 9, startMin = 0, endHour = 17, endMin = 0;
    if (startTime && startTime.includes(':')) {
      const startParts = startTime.split(':').map(Number);
      startHour = startParts[0] || 9;
      startMin = startParts[1] || 0;
    }
    if (endTime && endTime.includes(':')) {
      const endParts = endTime.split(':').map(Number);
      endHour = endParts[0] || 17;
      endMin = endParts[1] || 0;
    }
    
    // Calculate interval
    const startTimeInMinutes = startHour * 60 + startMin;
    const endTimeInMinutes = endHour * 60 + endMin;
    const firstWeekDays = endTimeInMinutes > startTimeInMinutes ? 6 : 7;
    
    // Calculate total days
    let totalDays;
    if (numWeeks === 1) {
      totalDays = firstWeekDays;
    } else {
      totalDays = firstWeekDays + (numWeeks - 1) * 7;
    }
    
    const startDateTime = moment(firstKey).hour(startHour).minute(startMin).second(0).millisecond(0);
    const endDateTime = startDateTime.clone().add(totalDays, 'days').hour(endHour).minute(endMin).second(0).millisecond(0);
    
    return {
      start: startDateTime.toDate(),
      end: endDateTime.toDate()
    };
  }

  // Find the first week and use effectiveStart if it's the current week
  const firstWeek = selectedWeekData.reduce(
    (min, w) => (min.start < w.start ? min : w),
    selectedWeekData[0],
  );
  let start = firstWeek.isCurrentWeek ? firstWeek.effectiveStart : firstWeek.start;
  
  // Find the last week and use effectiveEnd if it's the current week
  const lastWeek = selectedWeekData.reduce(
    (max, w) => (max.end > w.end ? max : w),
    selectedWeekData[0],
  );
  let end = lastWeek.isCurrentWeek ? lastWeek.effectiveEnd : lastWeek.end;
  
  // Calculate actual start and end datetimes based on selected times
  if (startTime && endTime && startTime.includes(':') && endTime.includes(':')) {
    const startParts = startTime.split(':').map(Number);
    const endParts = endTime.split(':').map(Number);
    
    // Validate that we have valid numbers
    const startHour = startParts[0];
    const startMin = startParts[1] || 0;
    const endHour = endParts[0];
    const endMin = endParts[1] || 0;
    
    // Skip if any values are NaN
    if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) {
      return { start, end };
    }
    
    // Compare time-of-day only (not total hours)
    const startTimeInMinutes = startHour * 60 + startMin;
    const endTimeInMinutes = endHour * 60 + endMin;
    
    // Create full datetime for start
    const startDateTime = moment(start).hour(startHour).minute(startMin).second(0).millisecond(0);
    
    // Calculate total days to add based on time comparison
    let totalDaysToAdd;
    const numWeeks = selectedWeeks.length;
    
    if (endTimeInMinutes > startTimeInMinutes) {
      // If drop-off time > pickup time: first week is 6 days, subsequent weeks are 7 days
      // Total = 6 + (numWeeks - 1) * 7 = 7 * numWeeks - 1
      totalDaysToAdd = (numWeeks * 7) - 1;
    } else {
      // If drop-off time ≤ pickup time: all weeks are 7 days
      totalDaysToAdd = numWeeks * 7;
    }
    
    // Calculate end datetime
    const endDateTime = startDateTime.clone()
      .add(totalDaysToAdd, 'days')
      .hour(endHour)
      .minute(endMin)
      .second(0)
      .millisecond(0);
    
    // Return the actual datetime objects (the calendar date is extracted from these)
    start = startDateTime.toDate();
    end = endDateTime.toDate();
  }
  
  return { start, end };
};


export const weekContainsUnavailableDates = (
  week: WeekData,
  unavailableDates: Date[],
  partialUnavailability: { [key: string]: { from: string; to: string }[] } = {},
  allowPartialFirstWeek: boolean = false
): boolean => {
  const weekStart = moment(week.isCurrentWeek ? week.effectiveStart : week.start);
  const weekEnd = moment(week.isCurrentWeek ? week.effectiveEnd : week.end);
  if (allowPartialFirstWeek) {
    const totalDays = weekEnd.diff(weekStart, "days") + 1;
    let unavailableDaysCount = 0;
    let currentDay = weekStart.clone();
    while (currentDay.isSameOrBefore(weekEnd, "day")) {
      const dayStr = currentDay.format("YYYY-MM-DD");
      const isFullyUnavailable = unavailableDates.some(
        (unavailableDate) =>
          moment(unavailableDate).format("YYYY-MM-DD") === dayStr
      );
      const isPartiallyUnavailable = partialUnavailability[dayStr] !== undefined;
      
      if (isFullyUnavailable || isPartiallyUnavailable) {
        unavailableDaysCount++;
      }
      
      currentDay.add(1, "day");
    }
    return unavailableDaysCount === totalDays;
  }
  
  for (const unavailableDate of unavailableDates) {
    const unavailableMoment = moment(unavailableDate);
    if (
      unavailableMoment.isSameOrAfter(weekStart, "day") &&
      unavailableMoment.isSameOrBefore(weekEnd, "day")
    ) {
      return true;
    }
  }

  let currentDay = weekStart.clone();
  while (currentDay.isSameOrBefore(weekEnd, "day")) {
    const dayStr = currentDay.format("YYYY-MM-DD");
    // For the current week, allow partial unavailability on the FIRST day only
    // (user can still select the week, just restricted pickup times)
    const isFirstDayOfCurrentWeek = week.isCurrentWeek && currentDay.isSame(weekStart, "day");
    
    if (partialUnavailability[dayStr] !== undefined && !isFirstDayOfCurrentWeek) {
      return true;
    }
    currentDay.add(1, "day");
  }

  return false;
};
